import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { withPasswordManagerRefresh } from "./passwordManagerRequest";
import { extractPasswordManagerError, getPasswordManagerAuthHeaders, passwordManagerUrl } from "./personaApi";
import { ICredentialPersona } from "./resetAutomationTypes";

export interface IGetCredentialPersonaParams {
	credentialId: number;
}

interface ICredentialPersonaState {
	data: ICredentialPersona | null;
	loading: boolean;
	error: string | null;
}

const initialState: ICredentialPersonaState = {
	data: null,
	loading: false,
	error: null,
};

export const getCredentialPersona = createAsyncThunk<ICredentialPersona | null, IGetCredentialPersonaParams>(
	"passwordManager/getCredentialPersona",
	async (params, { rejectWithValue, dispatch }) => {
		try {
			const res = await withPasswordManagerRefresh(dispatch, () =>
				axios.get<ICredentialPersona>(passwordManagerUrl(`/Credentials/${params.credentialId}/persona`), {
					headers: getPasswordManagerAuthHeaders(),
				}),
			);
			return res.data ?? null;
		} catch (error: unknown) {
			if (axios.isAxiosError(error) && error.response?.status === 404) {
				return null;
			}
			return rejectWithValue(extractPasswordManagerError(error, "Failed to load credential persona"));
		}
	},
);

const credentialPersonaSlice = createSlice({
	name: "passwordManager/credentialPersona",
	initialState,
	reducers: {
		resetCredentialPersona: (state) => {
			state.data = null;
			state.error = null;
			state.loading = false;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(getCredentialPersona.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(getCredentialPersona.fulfilled, (state, action) => {
				state.loading = false;
				state.data = action.payload;
			})
			.addCase(getCredentialPersona.rejected, (state, action) => {
				state.loading = false;
				state.data = null;
				state.error = (action.payload as string) || action.error.message || "Failed to load credential persona";
			});
	},
});

export const { resetCredentialPersona } = credentialPersonaSlice.actions;
export const credentialPersonaReducer = credentialPersonaSlice.reducer;
